/**
 * @namespace helpers
 */

/**
 * Helper functions for Entities
 *
 * @module entity
 * @memberof helpers
 * @see helpers
 */

import { noop, complement } from './fn'
import { property, pick, ascBy, descBy } from './object'

/**
 * Return value or NaN, if value is undefined
 *
 * @function
 * @param  {Object} n
 * @return {Object|Number}
 */
const definedOrNaN = (n) =>
  n !== undefined
    ? n
    : NaN

/**
 * Return entity id
 *
 * @function
 * @param  {models.Entity|Number} o
 * @return {Number} entity id (or number itself)
 */
export const getId = (o) =>
  typeof o === 'number'
    ? o
    : definedOrNaN(property('id')(o))

/**
 * Return entity name
 *
 * @function
 * @param  {models.Entity} o
 * @return {String} entity name
 */
export const getName = property('name')

/**
 * Return entity network
 *
 * @function
 * @param  {models.Entity} o
 * @return {String} entity network
 */
export const getNetwork = property('network')

/**
 * Return 'enabled' field from entity
 *
 * @param  {models.Entity} o
 * @return {Boolean}
 */
export const isEnabled = (o) =>
  o != null
    ? !!o.enabled
    : false

/**
 * Return reversed 'enabled' field from entity
 *
 * @param  {models.Entity} o
 * @return {Boolean}
 */
export const isDisabled = complement(isEnabled)

/**
 * Ascending sort function to use in Array.prototype.sort(...), sort by 'sortOrder' field
 *
 * @param  {models.Entity} o
 * @return {Boolean}
 */
export const ascSortOrder = ascBy(property('sortOrder'))

/**
 * Descending sort function to use in Array.prototype.sort(...), sort by 'sortOrder' field
 *
 * @param  {models.Entity} o
 * @return {Boolean}
 */
export const descSortOrder = descBy(property('sortOrder'))

/**
 * Return any entity field value
 *
 * @function
 * @param  {String} name
 * @param  {models.Entity} o
 * @return {Object} entity field value
 */
export const field = (name) =>
  name === 'id'
    ? getId
    : property(name)

/**
 * Returns partial copy of Entity containing only the keys specified
 *
 * @function
 * @param  {String[]} names - fields' names
 * @param  {models.Entity} o
 * @return {Object} partial copy of Entity
 */
export const pickFields = (...names) => (o) =>
  typeof o !== 'number'
    ? pick(...names)(o)
    : names.indexOf('id') > -1
      ? { id: o }
      : {}

/**
 * Return object with entity id only
 *
 * @function
 * @param  {models.Entity|Number} o
 * @return {Object} object with id field only
 */
export const pickId = pickFields('id')

/**
 * Find entity index in array by id
 *
 * @function
 * @param  {models.Entity[]} arr
 * @param  {Number} eid - entity id
 * @return {Number|models.Entity} entity index (or -1 if there is no such entity in array)
 */
export const indexIn = (arr) =>
  arr == null || !arr.length
    ? () => -1
    : (eid) =>
      eid != null
        ? arr.map(getId).indexOf(getId(eid))
        : -1

/**
 * Find Entity in array by entity id
 *
 * @function
 * @param  {models.Entity[]} arr
 * @param  {Number} e - entity id
 * @return {models.Entity} entity, or undefined
 */
export const findIn = (arr) =>
  arr == null || !arr.length
    ? noop
    : (e) =>
      arr[indexIn(arr)(e)]

/**
 * Remove entity from array
 *
 * @function
 * @param  {models.Entity[]} arr
 * @param  {Number} e - entity id
 */
export const remove = (arr) =>
  arr == null || !arr.length
    ? () => noop
    : (e) =>
      e == null
        ? noop
        : () => {
          let index = indexIn(arr)(e)
          if (index !== -1) {
            return arr.splice(index, 1)
          }
        }

/**
 * Add entity to array
 *
 * @function
 * @param  {models.Entity[]} arr
 * @param  {models.Entity}   e
 */
export const add = (arr) =>
  arr == null
    ? () => noop
    : (e) =>
      e == null
        ? noop
        : () => arr.push(e)

/**
 * Replace entity in array
 *
 * @function
 * @param  {models.Entity[]} arr
 * @param  {Boolean} [replace] - if true, entity is replaced completely, if false - new entity copied to old one
 * @param  {models.Entity}   e
 */
export const replace = (arr) =>
  arr == null
    ? () => noop
    : (replace = true) => (e) => {
      if (e != null) {
        let index = indexIn(arr)(e)
        if (index !== -1) {
          if (replace) {
            arr[index] = e
          } else {
            if (typeof arr[index]._weakCopy === 'function') {
              arr[index]._weakCopy(e)
            } else {
              Object.assign(arr[index], e)
            }
          }
        } else {
          arr.push(e)
        }
      }
    }

/**
 * Function to use in [].filter method to leave only unique entities
 *
 * @function
 * @param  {models.Entity}   e   - entity
 * @param  {Number}          i   - entity index within array
 * @param  {models.Entity[]} arr - array of entities
 * @return {Boolean}
 */
export const unique = (e, i, arr) =>
  e && indexIn(arr)(e) === i

/**
 * Returns object with fields 'key' and 'value', used in .map()
 * By default key = id, value = name
 *
 * @function
 * @param  {Object} getters - property getters
 * @param  {Function} getters.key - key getter
 * @param  {Function} getters.value - value getter
 * @param  {Object} o - object/entity
 * @return {Object} { key,value } object
 */
export const keyValue = ({ key = getId, value = getName } = {}) => (o) =>
  ({
    key: key(o),
    value: value(o)
  })
